/**
 * LivePill — floating "LIVE" affordance centred just above the tab bar.
 * -------------------------------------------------------------------------
 * Appears ONLY while an F1 session is happening (status 'live' or 'locked'),
 * and hides otherwise — that on/off is the whole point of the feature. Tapping
 * it opens the live track map. The reserved fourth-tab slot stays reserved;
 * this rides above the three-tab bar rather than becoming a permanent tab, so
 * the nav doesn't shift when nothing's on.
 *
 * The container is pointer-events-none so it never intercepts taps meant for
 * the screen beneath; only the button itself is interactive.
 */
export default function LivePill({ status, session, onOpen }) {
  if (status !== 'live' && status !== 'locked') return null;

  const sessionName = session?.session_name || 'Session';
  const where = session?.circuit_short_name || session?.location || '';
  const label = status === 'live' ? [sessionName, where].filter(Boolean).join(' · ') : 'Session in progress';

  return (
    <div
      aria-live="polite"
      className="pointer-events-none absolute inset-x-0 z-20 flex justify-center"
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 4.75rem)' }}
    >
      <button
        onClick={onOpen}
        aria-label={`Open live track map — ${label}`}
        className="pointer-events-auto pressable no-select flex items-center gap-2.5 rounded-full border border-f1-red/40 bg-bg-elevated/95 py-2 pl-3 pr-4 shadow-glow-red backdrop-blur-md animate-slide-up"
      >
        <span className="relative flex h-2.5 w-2.5 items-center justify-center">
          <span className="absolute inline-flex h-2.5 w-2.5 rounded-full bg-f1-red animate-pulse-red" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-f1-red" />
        </span>
        <span className="text-2xs font-bold uppercase tracking-[0.18em] text-f1-red">Live</span>
        <span className="max-w-[11rem] truncate text-xs font-semibold text-ink-primary">{label}</span>
      </button>
    </div>
  );
}
